import { MagnifyingGlassIcon, XCircleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';

import { getContacts } from '../../api/contacts';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { Input } from '../../components/Form';
import { getUserFullName } from '../../components/helpers/functions';

import useAsync from '../../hooks/useAsync';

import { AvatarGenerated } from '../../components/Avatar';

const ContactsList = () => {
	const navigate = useNavigate();
	const [searchFilter, setSearchFilter] = useState('');

	const { value: contacts } = useAsync(getContacts);

	const sortObject = (o) =>
		Object.keys(o)
			.sort()
			.reduce((r, k) => ((r[k] = o[k]), r), {});

	const getTitle = ({ type, title, company, location }) => {
		if (type === 'person') {
			if (company && title) {
				return `${title} at ${company.companyName}`;
			} else if (company && !title) {
				return `from ${company.companyName}`;
			} else if (!company && title) {
				return title;
			}
		} else if (type === 'company') {
			if (location) {
				return `${location && ` from ${location}`}`;
			}
			return null;
		}
		return null;
	};

	const sorted =
		contacts?.rows
			?.filter((contact) => {
				if (searchFilter) {
					const search = searchFilter.toLowerCase();
					return (
						contact?.firstName?.toLowerCase()?.indexOf(search) > -1 ||
						contact?.lastName?.toLowerCase()?.indexOf(search) > -1 ||
						contact?.companyName?.toLowerCase()?.indexOf(search) > -1
					);
				}
				return contact;
			})
			.reduce((groups, item) => {
				const directoryLetter = (
					item?.lastName ||
					item?.firstName ||
					item?.companyName
				)
					?.substring(0, 1)
					.toUpperCase();

				return {
					...groups,
					[directoryLetter]: [...(groups[directoryLetter] || []), item],
				};
			}, {}) || [];

	return (
		<MainContent maxWidth="w-full">
			<div className="pb-4">
				<PageHeading
					actions={[
						{
							name: 'Add Contact',
							type: 'primary',
							onClick: () => navigate('/manage/directory/new'),
						},
					]}
					divider={false}
				>
					Directory
				</PageHeading>
			</div>
			<div className="flex flex-col lg:flex-row gap-6">
				{/* Sidebar */}
				<div className="w-full lg:w-80 flex-shrink-0 border-r border-gray-200">
					<Input
						placeholder={'Search contacts...'}
						onChange={(e) => {
							setSearchFilter(e?.target?.value);
						}}
						PreIcon={MagnifyingGlassIcon}
						PostIcon={searchFilter ? XCircleIcon : null}
						onClickPostIcon={() => setSearchFilter('')}
						value={searchFilter}
					/>
					<nav
						className="mt-4 max-h-[calc(100vh-250px)] overflow-y-auto"
						aria-label="Directory"
					>
						{Object.keys(sortObject(sorted)).map((letter) => (
							<div key={letter} className="relative">
								<div className="sticky top-0 z-10 border-t border-b border-gray-200 bg-gray-50 px-4 py-1 text-sm font-medium text-gray-500">
									<h3>{letter}</h3>
								</div>
								<ul role="list" className="relative z-0 divide-y divide-gray-200">
									{sorted[letter]
										.map((c) => {
											const label =
												c?.type === 'person'
													? getUserFullName(c)
													: c?.companyName;

											return {
												...c,
												label,
											};
										})
										.sort((a, b) => a.label.localeCompare(b.label))
										.map((contact) => {
											return (
												<li
													key={contact.id}
													onClick={() => {
														setSearchFilter('');
														navigate(`/manage/directory/${contact.id}`);
													}}
												>
													<div className="cursor-pointer relative flex items-center space-x-3 px-4 py-4 focus-within:ring-2 focus-within:ring-inset focus-within:ring-pink-500 hover:bg-gray-50">
														<div className="flex-shrink-0">
															{contact?.type === 'person' && (
																<AvatarGenerated
																	size="xs"
																	id={getUserFullName(contact)}
																/>
															)}
															{contact?.type === 'company' && (
																<AvatarGenerated
																	size="xs"
																	id={contact.id}
																	type="company"
																/>
															)}
														</div>
														<div className="min-w-0 flex-1">
															<a className="focus:outline-none">
																<span
																	className="absolute inset-0"
																	aria-hidden="true"
																/>
																<p className="text-sm font-medium text-gray-900">
																	{contact?.label}
																</p>
																<p className="truncate text-xs text-gray-500">
																	{getTitle(contact)}
																</p>
															</a>
														</div>
													</div>
												</li>
											);
										})}
								</ul>
							</div>
						))}
					</nav>
				</div>
				{/* Profile content */}
				<div className="flex-1 min-w-0">
					<Outlet />
				</div>
			</div>
		</MainContent>
	);
};

export default ContactsList;
